import type { ServiceRequest } from '@medplum/fhirtypes';
import React from 'react';

import { Icon, Stack, Tag, Text } from '@eluve/components';
import {
  type FhirPageControls,
  type FhirSectionStatus,
  LoadMoreRow,
  SectionBoundary,
} from '@eluve/fhir-data';
import { formatFhirDate } from '@eluve/fhir-renderer';

import { isPendingReview } from '../review-markers';
import { todoDueDate } from '../todo-list';

/**
 * The Overview-tab Patient to-dos card (ENG-5368): the patient's
 * home-program ServiceRequests written by Care Plan activation. Deliberately
 * read-only — no check-off until the adherence model lands (ENG-5382).
 * Callers pass the already-filtered visible list (see todo-list.ts).
 */
export type TodosCardProps = {
  todos: ServiceRequest[];
  status: FhirSectionStatus;
  paging: FhirPageControls;
  onOpen: (serviceRequestId: string) => void;
};

const TodoRow: React.FC<{
  todo: ServiceRequest;
  onOpen: () => void;
}> = ({ todo, onOpen }) => {
  const done = todo.status === 'completed';
  const pending = isPendingReview(todo);
  const dueDate = todoDueDate(todo);
  const fromCarePlan = Boolean(todo.basedOn?.length);

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="patient-todo-row"
      className="relative flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-left not-first:before:absolute not-first:before:inset-x-1 not-first:before:top-0 not-first:before:border-t not-first:before:border-border-primary hover:bg-bg-secondary not-first:hover:before:opacity-0 [:hover+&]:before:opacity-0"
    >
      <div className="min-w-0 flex-1">
        <Stack
          direction="horizontal"
          gap={2}
          align="baseline"
          fullWidth={false}
          className="min-h-6"
        >
          <Text
            inline
            size="sm"
            weight="medium"
            color={done ? 'tertiary' : undefined}
            className={done ? 'truncate line-through' : 'truncate'}
          >
            {todo.code?.text ?? '(unnamed to-do)'}
          </Text>
          {pending && (
            <Tag pill subtle size="xs" color="insights" label="Unverified" />
          )}
        </Stack>
        <Text size="xs" color="tertiary" weight="medium" className="mt-0.5">
          {[
            done ? 'Done' : undefined,
            dueDate ? `Due: ${formatFhirDate(dueDate)}` : undefined,
            fromCarePlan ? 'From care plan' : undefined,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      </div>
      <Icon
        name="ChevronRight"
        size="xs"
        className="shrink-0 text-text-tertiary"
      />
    </button>
  );
};

export const TodosCard: React.FC<TodosCardProps> = ({
  todos,
  status,
  paging,
  onOpen,
}) => {
  return (
    <section
      data-testid="patient-todos-card"
      className="rounded-lg bg-bg-primary shadow-xs ring-1 ring-black/5"
    >
      <Stack
        direction="horizontal"
        gap={2}
        align="center"
        justify="between"
        className="h-12 px-4"
      >
        <Text inline size="sm" weight="semibold">
          Patient To-dos
        </Text>
      </Stack>
      <div className="px-4">
        <SectionBoundary status={status} emptyText="No patient to-dos.">
          <div className="-mx-3 py-1">
            {todos.map((todo) => (
              <TodoRow
                key={todo.id}
                todo={todo}
                onOpen={() => todo.id && onOpen(todo.id)}
              />
            ))}
            <LoadMoreRow paging={paging} noun="to-dos" />
          </div>
        </SectionBoundary>
      </div>
    </section>
  );
};
